import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import API from "../config/api.js";

const MyMediaUploadContext = createContext(null);

export function MyMediaUploadProvider({ children }) {
  // ── refs para os hidden inputs renderizados no MyMediaUploadManager ────────
  const imageInputRef    = useRef(null);
  const reelInputRef     = useRef(null);
  const combinedInputRef = useRef(null);

  // ── sessão atual (definida por openUpload) ─────────────────────────────────
  const sessionRef = useRef({ prompt: "0", onUploaded: null, onCancelled: null });

  // ── estado do modal de imagem ──────────────────────────────────────────────
  const [imageModal, setImageModal] = useState({ open: false, files: [] });
  // files: [{ file, dataUrl, angle }]

  // ── estado do modal de reel ────────────────────────────────────────────────
  const [reelModal, setReelModal] = useState({ open: false, progress: 0 });

  // ── flags de envio em andamento (previne cliques duplos / chamadas reentrantes) ──
  const [submittingImages, setSubmittingImages] = useState(false);
  const [submittingReel,   setSubmittingReel]   = useState(false);

  // refs espelham o estado para guard síncrono dentro das callbacks
  const submittingImagesRef = useRef(false);
  const submittingReelRef   = useRef(false);

  // ── openUpload — disparo principal ────────────────────────────────────────
  // type   : 'image' | 'reel' | 'combined'
  // prompt : '0' redirect my-media | '1' onUploaded callback | '2' onUploaded callback
  const openUpload = useCallback(({ type = "combined", prompt = "0", onUploaded = null, onCancelled = null } = {}) => {
    sessionRef.current = { prompt: String(prompt), onUploaded, onCancelled };

    if (type === "image")    imageInputRef.current?.click();
    else if (type === "reel") reelInputRef.current?.click();
    else                      combinedInputRef.current?.click();
  }, []);

  // ── helpers de imagem ──────────────────────────────────────────────────────
  const loadImageFiles = useCallback((fileList) => {
    const files = Array.from(fileList).slice(0, 10);
    if (!files.length) return;

    let loaded = 0;
    const previews = Array(files.length);

    files.forEach((file, idx) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        previews[idx] = { file, dataUrl: e.target.result, angle: 0 };
        loaded++;
        if (loaded === files.length) {
          setImageModal({ open: true, files: previews });
        }
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const rotateImage = useCallback((idx) => {
    setImageModal((prev) => ({
      ...prev,
      files: prev.files.map((f, i) =>
        i === idx ? { ...f, angle: (f.angle + 90) % 360 } : f
      ),
    }));
  }, []);

  const closeImageModal = useCallback((cancelled = false) => {
    setImageModal({ open: false, files: [] });
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (cancelled) sessionRef.current.onCancelled?.();
  }, []);

  const submitImages = useCallback(async (files) => {
    // guard reentrante — ignora cliques duplos / chamadas concorrentes
    if (submittingImagesRef.current) return;
    submittingImagesRef.current = true;
    setSubmittingImages(true);

    const { prompt, onUploaded } = sessionRef.current;

    try {
      for (let i = 0; i < files.length; i++) {
        const { file, angle } = files[i];
        const fd = new FormData();
        fd.append("file", file);
        fd.append("position", "middle");
        fd.append("angle", String(angle));

        try {
          const res  = await fetch(`${API.drafts}?addImage`, { method: "POST", credentials: "include", body: fd });
          const data = await res.json();
          if (data.status === "success") onUploaded?.(data.idimage, "image");
        } catch (err) {
          console.error("Image upload error:", err);
        }
      }

      closeImageModal();
      if (prompt === "0") window.location.reload();
    } finally {
      submittingImagesRef.current = false;
      setSubmittingImages(false);
    }
  }, [closeImageModal]);

  // ── helpers de reel ────────────────────────────────────────────────────────
  const closeReelModal = useCallback((cancelled = false) => {
    setReelModal({ open: false, progress: 0 });
    if (reelInputRef.current) reelInputRef.current.value = "";
    if (cancelled) sessionRef.current.onCancelled?.();
  }, []);

  const uploadReel = useCallback((file) => {
    // guard reentrante — evita uploads simultâneos do mesmo reel
    if (submittingReelRef.current) return;
    submittingReelRef.current = true;
    setSubmittingReel(true);

    const { prompt, onUploaded } = sessionRef.current;

    setReelModal({ open: true, progress: 0 });

    const fd = new FormData();
    fd.append("file", file);
    fd.append("prompt", prompt);

    const xhr = new XMLHttpRequest();

    const finish = () => {
      submittingReelRef.current = false;
      setSubmittingReel(false);
      closeReelModal();
    };

    xhr.upload.addEventListener("progress", (evt) => {
      if (evt.lengthComputable) {
        setReelModal((prev) => ({ ...prev, progress: Math.min((evt.loaded / evt.total) * 90, 90) }));
      }
    });

    xhr.addEventListener("load", () => {
      setReelModal((prev) => ({ ...prev, progress: 100 }));
      try {
        const data = JSON.parse(xhr.responseText);
        if (data.status === "success") {
          onUploaded?.(data.idreel, "reel");
          if (prompt === "0") window.location.reload();
        }
      } catch (err) {
        console.error("Reel response error:", err);
      }
      finish();
    });

    xhr.addEventListener("error", () => {
      console.error("Reel upload failed");
      finish();
    });

    xhr.addEventListener("abort", () => {
      finish();
    });

    xhr.withCredentials = true;
    xhr.open("POST", `${API.drafts}?addReel`);
    xhr.send(fd);
  }, [closeReelModal]);

  // ── combined ───────────────────────────────────────────────────────────────
  const handleCombinedFiles = useCallback((fileList) => {
    const all    = Array.from(fileList);
    const images = all.filter((f) => f.type.startsWith("image/"));
    const reels  = all.filter((f) => f.type === "video/mp4");

    if (images.length) loadImageFiles(images);
    reels.forEach((file, i) => setTimeout(() => uploadReel(file), i * 5000));

    if (combinedInputRef.current) combinedInputRef.current.value = "";
  }, [loadImageFiles, uploadReel]);

  // ── expõe no window para acesso via console ────────────────────────────────
  useEffect(() => {
    window._upload = {
      open:     (opts)       => openUpload(opts),
      image:    (onUploaded) => openUpload({ type: "image",    prompt: "1", onUploaded }),
      reel:     (onUploaded) => openUpload({ type: "reel",     prompt: "1", onUploaded }),
      combined: (onUploaded) => openUpload({ type: "combined", prompt: "1", onUploaded }),
    };
    return () => { delete window._upload; };
  }, [openUpload]);

  return (
    <MyMediaUploadContext.Provider
      value={{
        openUpload,
        imageModal,
        reelModal,
        submittingImages,
        submittingReel,
        imageInputRef,
        reelInputRef,
        combinedInputRef,
        loadImageFiles,
        rotateImage,
        closeImageModal,
        submitImages,
        uploadReel,
        handleCombinedFiles,
      }}
    >
      {children}
    </MyMediaUploadContext.Provider>
  );
}

export function useMyMediaUpload() {
  return useContext(MyMediaUploadContext);
}